import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { ArrowDown, ArrowUp, Plus, Send, X } from "lucide-react";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useShop } from "../../contexts/ShopContext";
import { GET_SUBSCRIPTION_STATUS } from "../../graphql/queries";
import { summarizeSubscription } from "../../utils/subscription";
import { GSC_TAB_CONFIG } from "../../components/reportViewers/GscTabConfig";
import { REVENUE_REALITY_TAB_CONFIG } from "../../components/reportViewers/RevenueRealityTabConfig";
import ReportViewerContent from "../ReportsPage/ReportViewerContent";
import { FREE_LOCKED_REVENUE_TABS, getReportTitle } from "../ReportsPage/reportViewUtils";
import ChatPage from "../ChatPage";
import {
  getReportById as fetchReportById,
  fetchMetaOverallReport,
  fetchMetaRawDataReport,
  fetchConversionAndPdpAssessmentReport,
  fetchHomepageAssessmentReport,
  fetchSocialMediaAssessmentReport,
  fetchSocialMediaRawReport,
  forceRefreshReport,
  refreshGscReport,
} from "../../utils/api/reportApi";
import { getChannelChatContextOptions } from "../../utils/api/channelChatApi";
import {
  readDraggedChatContext,
  sanitizeChatContext,
  setChatContextPickMode,
} from "../../utils/chatContextDrag";
import { ASSESSMENT_SUMMARY_ITEM_BY_ID } from "../../utils/assessmentSummaryConfig";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./ReportDetailPage.module.css";

const DRAG_START_MOUSE_KEY = "__zeqiReportDetailStartMouseDrag";
const DRAG_START_TOUCH_KEY = "__zeqiReportDetailStartTouchDrag";

const REPORT_CHAT_CONTEXT_MAP = {
  benchmark: {
    channelId: "main",
    trail: [{ key: "main", label: "Main", type: "main" }],
  },
  shop_meta: {
    channelId: "paid-ads",
    subchannelId: "meta-ads",
    trail: [
      { key: "paid-ads", label: "Paid Ads", type: "channel" },
      { key: "meta-ads", label: "Meta Ads", type: "subchannel" },
    ],
  },
  revenue_reality_full_report: {
    channelId: "paid-ads",
    trail: [{ key: "paid-ads", label: "Paid Ads", type: "channel" }],
  },
  google_search_console: {
    channelId: "google",
    subchannelId: "google-search-console",
    trail: [
      { key: "google", label: "Google", type: "channel" },
      { key: "google-search-console", label: "Google Search Console", type: "subchannel" },
    ],
  },
  google_merchant_center: {
    channelId: "google",
    subchannelId: "google-merchant-center",
    trail: [
      { key: "google", label: "Google", type: "channel" },
      { key: "google-merchant-center", label: "Google Merchant Center", type: "subchannel" },
    ],
  },
  target_audience: {
    channelId: "paid-ads",
    trail: [{ key: "paid-ads", label: "Paid Ads", type: "channel" }],
  },
  competitor_analysis: {
    channelId: "social",
    trail: [{ key: "social", label: "Social Media", type: "channel" }],
  },
  market_analysis: {
    channelId: "seo",
    trail: [{ key: "seo", label: "SEO / AEO", type: "channel" }],
  },
  yearly_content_calendar: {
    channelId: "content-cal",
    trail: [{ key: "content-cal", label: "Content Calendar", type: "channel" }],
  },
  conversion_and_pdp_report: {
    channelId: "pdp",
    trail: [{ key: "pdp", label: "PDP", type: "channel" }],
  },
  sales_orders_rolling_report: {
    channelId: "main",
    trail: [{ key: "main", label: "Main", type: "main" }],
  },
};

const BENCHMARK_VIEW_CONFIG = {
  "brand-assets": { label: "Brand assets", type: "knowledge" },
  "tone-and-voice": { label: "Tone & voice", type: "knowledge" },
};

const REPORT_CHAT_CONTEXT_STORAGE_PREFIX = "zeqi:report-chat-extra-contexts";

const reportChatContextStorageKey = (userId, reportId) =>
  `${REPORT_CHAT_CONTEXT_STORAGE_PREFIX}:${userId || "anon"}:${reportId || "unknown"}`;

const readStoredReportChatContexts = (userId, reportId) => {
  if (!userId || !reportId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(reportChatContextStorageKey(userId, reportId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => item?.key) : [];
  } catch {
    return [];
  }
};

const writeStoredReportChatContexts = (userId, reportId, contexts = []) => {
  if (!userId || !reportId || typeof window === "undefined") return;
  try {
    const key = reportChatContextStorageKey(userId, reportId);
    const clean = Array.isArray(contexts) ? contexts.filter((item) => item?.key) : [];
    if (clean.length) {
      window.localStorage.setItem(key, JSON.stringify(clean));
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // best-effort only
  }
};

const buildReportWindowChatContext = ({ reportId, collectionName, reportTitle, windowInfo }) => {
  if (!windowInfo) return null;
  const key =
    windowInfo.windowKey ||
    windowInfo.key ||
    windowInfo.label ||
    [windowInfo.startDate, windowInfo.endDate].filter(Boolean).join("_") ||
    "";
  if (!key) return null;
  const label =
    windowInfo.label ||
    (windowInfo.startDate && windowInfo.endDate
      ? `${windowInfo.startDate} to ${windowInfo.endDate}`
      : key);
  return {
    key: `report-window:${reportId || "report"}:${key}`,
    label: `Window: ${label}`,
    type: "report-window",
    reportId: reportId || null,
    collectionName: collectionName || null,
    reportTitle: reportTitle || null,
    windowKey: key,
    windowLabel: label,
    window: {
      key,
      label,
      days: windowInfo.days ?? null,
      startDate: windowInfo.startDate || null,
      endDate: windowInfo.endDate || null,
      assessmentLabel: windowInfo.assessmentLabel || null,
    },
  };
};

const REPORT_CONTENT_CONTEXT_MAP = {
  foundational_business_reality_summary: ({ summaryId }) => {
    const summaryItem = summaryId ? ASSESSMENT_SUMMARY_ITEM_BY_ID[summaryId] : null;
    return summaryItem
      ? { key: `summary:${summaryItem.id}`, label: summaryItem.label, type: "assessment-summary" }
      : null;
  },
  shop_meta: () => ({ key: 'meta-tab:raw_data', label: 'Raw Data', type: 'report-section' }),
  google_search_console: ({ activeGscTab }) => GSC_TAB_CONFIG.find((tab) => tab.key === activeGscTab)
    ? {
        key: `gsc-tab:${activeGscTab}`,
        label: GSC_TAB_CONFIG.find((tab) => tab.key === activeGscTab).label,
        type: "report-section",
      }
    : null,
  revenue_reality_full_report: ({ activeRevenueTab }) => REVENUE_REALITY_TAB_CONFIG.find((tab) => tab.key === activeRevenueTab)
    ? {
        key: `revenue-tab:${activeRevenueTab}`,
        label: REVENUE_REALITY_TAB_CONFIG.find((tab) => tab.key === activeRevenueTab).label,
        type: "report-section",
      }
    : null,
};

const REFRESH_KEY_BY_COLLECTION = {
  shop_meta: "shop_meta",
  google_search_console: "google_search_console",
  google_merchant_center: "google_merchant_center",
  revenue_reality_full_report: "revenue_reality_full_report",
  shop_market_analysis: "shop_market_analysis",
  shop_target_audience: "shop_target_audience",
  shop_daily_overview: "shop_daily_overview",
  shop_benchmark_report: "shop_benchmark_report",
  homepage_assessment: "shop_benchmark_report",
  conversion_and_pdp_report: "conversion_and_pdp_report",
  sales_orders_rolling_report: "sales_orders_rolling_report",
};

const ReportDetailPage = () => {
  const { reportId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthUser();
  const { t } = useLocalization();
  const { setPageHeader } = usePageHeader();
  const { shopData } = useShop();
  const authToken = user?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const userId = user?._id || user?.id || user?.userId;
  const isMongoId = (v) => /^[a-f0-9]{24}$/i.test(String(v || ""));
  const shopId = isMongoId(shopData?._id) ? shopData._id : isMongoId(shopData?.id) ? shopData.id : null;
  const prefetchedReport = location.state?.prefetchedReport || null;
  const reportOpenTrace = location.state?.reportOpenTrace || null;
  const reportOpenTraceId = reportOpenTrace?.traceId || null;
  const fromDailyOverview = Boolean(location.state?.fromDailyOverview);
  const dailyOverviewReturnTo = location.state?.dailyOverviewReturnTo || "/reports";
  const dailyOverviewLabel = location.state?.dailyOverviewLabel || "Daily Overview";
  const prefetchedReportId = String(
    prefetchedReport?._id || prefetchedReport?.id || "",
  ).trim();
  const isPrefetchedMatch =
    Boolean(prefetchedReportId) &&
    prefetchedReportId === String(reportId || "").trim();
  const [activeGscTab, setActiveGscTab] = useState("indexing");
  const [activeRevenueTab, setActiveRevenueTab] = useState("revenue_reality");
  const splitRef = useRef(null);
  const contextMenuRef = useRef(null);
  const extraContextsHydratedRef = useRef(false);
  const windowFetchSeqRef = useRef(0);
  const lastWindowFetchKeyRef = useRef("");
  const [topFlex, setTopFlex] = useState(0.58);
  const [chatPaneState, setChatPaneState] = useState("default");
  const [reportViewerWindow, setReportViewerWindow] = useState(null);
  const [dismissedContextKeys, setDismissedContextKeys] = useState([]);
  const [extraChatContexts, setExtraChatContexts] = useState([]);
  const [contextOptions, setContextOptions] = useState([]);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [isContextDropActive, setIsContextDropActive] = useState(false);
  const [report, setReport] = useState(() => {
    const prefetchedId = String(
      prefetchedReport?._id || prefetchedReport?.id || "",
    ).trim();
    return prefetchedId && prefetchedId === String(reportId || "").trim()
      ? prefetchedReport
      : null;
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [rawMetaLoading, setRawMetaLoading] = useState(false);
  const [windowLoadingKey, setWindowLoadingKey] = useState(null);
  const [reportVersion, setReportVersion] = useState(0);
  const rawMetaLoadedRef = useRef(false);
  const homepageWindowsLoadKeyRef = useRef("");
  const readyLogRef = useRef(false);
  const isChatClosed = chatPaneState === "minimized";
  const handleExpandChat = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "minimized") return "default";
      if (current === "default") return "maximized";
      return "default";
    });
  }, []);
  const handleExpandReports = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "maximized") return "default";
      if (current === "default") return "minimized";
      return "minimized";
    });
    setTopFlex((current) => Math.max(current, 0.72));
  }, []);

  const { data: subData } = useQuery(GET_SUBSCRIPTION_STATUS, {
    skip: !authToken,
    fetchPolicy: "cache-and-network",
    context: { headers },
  });
  const subSummary = useMemo(
    () => summarizeSubscription(subData?.getSubscriptionStatus),
    [subData],
  );

  useEffect(() => {
    console.log("[report-open][frontend] detail.mount", {
      traceId: reportOpenTraceId,
      reportId,
      isPrefetchedMatch,
      source: reportOpenTrace?.source || null,
    });
  }, [reportOpenTraceId, reportId, isPrefetchedMatch, reportOpenTrace?.source]);

  useEffect(() => {
    let active = true;
    if (!reportId || !userId || !authToken) return undefined;

    // Only skip the overall fetch when the prefetched Meta payload already contains
    // the actual overall report data, not when it is only the lightweight summary.
    const hasPrefetchedMetaOverallPayload = Boolean(
      prefetchedReport?.data?.campaign_performance_totals ||
      prefetchedReport?.data?.campaign_level_assessment ||
      prefetchedReport?.data?.audience_efficiency_matrix_data ||
      prefetchedReport?.data?.ads_level_assessment
    );
    const isMetaOverallPrefetch =
      prefetchedReport?.data?.collection_name === "shop_meta" &&
      isPrefetchedMatch &&
      hasPrefetchedMetaOverallPayload &&
      reportVersion === 0;
    if (isMetaOverallPrefetch) {
      rawMetaLoadedRef.current = false;
      return undefined;
    }
    const isHomepageAssessmentPrefetch =
      prefetchedReport?.data?.collection_name === "homepage_assessment" &&
      isPrefetchedMatch &&
      Array.isArray(prefetchedReport?.data?.windows) &&
      prefetchedReport.data.windows.length > 0 &&
      reportVersion === 0;
    if (isHomepageAssessmentPrefetch) {
      return undefined;
    }
    const loadReport = async () => {
      console.log("[report-open][frontend] detail.fetch_start", {
        traceId: reportOpenTraceId,
        reportId,
        prefetched: Boolean(isPrefetchedMatch),
      });
      setReportLoading(true);
      setReportError(null);
      try {
        const isMetaReport = report?.data?.collection_name === "shop_meta";
        const isHomepageAssessmentReport =
          report?.data?.collection_name === "homepage_assessment" ||
          (isPrefetchedMatch && prefetchedReport?.data?.collection_name === "homepage_assessment");
        const isConversionPdpReport =
          report?.data?.collection_name === "conversion_and_pdp_report" ||
          (isPrefetchedMatch && prefetchedReport?.data?.collection_name === "conversion_and_pdp_report");
        if (isMetaReport && shopId) {
          // Section 1: totals — fast, unblocks the UI immediately
          const totalsResponse = await fetchMetaOverallReport({
            userId, shopId, authToken, traceId: reportOpenTraceId,
            query: { section: 'totals' },
          });
          if (!active) return;
          const totalsReport = totalsResponse?.report || null;
          setReport(totalsReport);
          setReportLoading(false);
          rawMetaLoadedRef.current = false;
          console.log("[report-open][frontend] detail.fetch_complete", {
            traceId: reportOpenTraceId,
            reportId,
            section: 'totals',
            fetchedReportId: totalsReport?._id || totalsReport?.id || null,
            collectionName: totalsReport?.data?.collection_name || null,
          });

          // Sections 2-4: merge each into state as they arrive
          for (const section of ['campaign_assessment', 'audience_matrix', 'ads_assessment']) {
            if (!active) break;
            try {
              const resp = await fetchMetaOverallReport({
                userId, shopId, authToken, traceId: reportOpenTraceId,
                query: { section },
              });
              if (!active) break;
              const sectionData = resp?.report?.data;
              if (sectionData) {
                setReport((prev) =>
                  prev ? { ...prev, data: { ...prev.data, ...sectionData } } : prev
                );
              }
            } catch {
              // silent — totals data is already showing
            }
          }
        } else if (isHomepageAssessmentReport && shopId) {
          const response = await fetchHomepageAssessmentReport({
            userId,
            shopId,
            authToken,
            traceId: reportOpenTraceId,
            query: { section: "assessment", windowKey: "last_7_days" },
          });
          if (!active) return;
          const fetchedReport = response?.report || null;
          setReport(fetchedReport);
          console.log("[report-open][frontend] detail.fetch_complete", {
            traceId: reportOpenTraceId,
            reportId,
            section: "homepage:last_7_days",
            fetchedReportId: fetchedReport?._id || fetchedReport?.id || null,
            collectionName: fetchedReport?.data?.collection_name || null,
          });
        } else if (isConversionPdpReport && shopId) {
          // Phase 1: show last_7_days immediately — use prefetch if warm, else fetch it.
          const hasColdPrefetch =
            isPrefetchedMatch &&
            prefetchedReport?.data?.collection_name === "conversion_and_pdp_report" &&
            Array.isArray(prefetchedReport?.data?.windows) &&
            prefetchedReport.data.windows.length > 0 &&
            reportVersion === 0;

          let initialReport = null;
          if (hasColdPrefetch) {
            initialReport = prefetchedReport;
          } else {
            const r = await fetchConversionAndPdpAssessmentReport({
              userId, shopId, authToken, traceId: reportOpenTraceId,
              reportId, windowKey: "last_7_days", rankingMode: "summary",
            });
            if (!active) return;
            initialReport = r?.report || null;
          }

          if (!active) return;
          setReport(initialReport);
          setReportLoading(false);
          console.log("[report-open][frontend] detail.fetch_complete", {
            traceId: reportOpenTraceId,
            reportId,
            section: "conversion:last_7_days",
            fetchedReportId: initialReport?._id || initialReport?.id || null,
            collectionName: initialReport?.data?.collection_name || null,
          });

          // Phase 2: load remaining windows one at a time, like Meta loads sections.
          // Each window merges into state as it arrives — the selector expands progressively.
          const gk = initialReport?.data?.generation_key || null;
          const resolvedReportId = initialReport?._id || initialReport?.id || reportId || null;
          const available = Array.isArray(initialReport?.data?.available_windows)
            ? initialReport.data.available_windows : [];
          const loadedSet = new Set(
            Array.isArray(initialReport?.data?.windows)
              ? initialReport.data.windows.map((w) => w?.key).filter(Boolean)
              : []
          );
          const toFetch = available
            .filter((w) => w?.key && !loadedSet.has(w.key))
            .sort((a, b) => Number(a?.days || 0) - Number(b?.days || 0));

          for (const windowEntry of toFetch) {
            if (!active) break;
            try {
              const resp = await fetchConversionAndPdpAssessmentReport({
                userId, shopId, authToken, traceId: reportOpenTraceId,
                reportId: resolvedReportId,
                windowKey: windowEntry.key,
                generationKey: gk,
                rankingMode: "summary",
              });
              if (!active) break;
              const newWindows = Array.isArray(resp?.report?.data?.windows)
                ? resp.report.data.windows : [];
              if (!newWindows.length) continue;
              setReport((prev) => {
                if (!prev?.data) return prev;
                const existing = Array.isArray(prev.data.windows) ? prev.data.windows : [];
                const existingKeys = new Set(existing.map((e) => e?.key).filter(Boolean));
                const additions = newWindows.filter((e) => e?.key && !existingKeys.has(e.key));
                if (!additions.length) return prev;
                return {
                  ...prev,
                  data: {
                    ...prev.data,
                    available_windows: prev.data.available_windows?.length
                      ? prev.data.available_windows
                      : resp.report.data.available_windows || [],
                    windows: [...existing, ...additions],
                  },
                };
              });
            } catch {
              // silent — last_7_days data is already visible
            }
          }
        } else {
          const response = await fetchReportById({ reportId, userId, authToken, traceId: reportOpenTraceId });
          if (!active) return;
          const fetchedReport = response?.report || null;
          setReport(fetchedReport);
          console.log("[report-open][frontend] detail.fetch_complete", {
            traceId: reportOpenTraceId,
            reportId,
            fetchedReportId: fetchedReport?._id || fetchedReport?.id || null,
            collectionName: fetchedReport?.data?.collection_name || null,
          });
        }
      } catch (error) {
        if (!active) return;
        setReportError(error);
        setReport((current) => current || null);
        console.log("[report-open][frontend] detail.fetch_error", {
          traceId: reportOpenTraceId,
          reportId,
          error: error?.message || String(error),
        });
      } finally {
        if (active) {
          setReportLoading(false);
        }
      }
    };

    loadReport();

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, userId, authToken, reportOpenTraceId, isPrefetchedMatch, shopId, reportVersion]);
  const resolvedReport = report;
  const hasFullMetaPayload = Boolean(
    resolvedReport?.data?.campaign_performance_raw?.months?.length,
  );
  const metaAvailableMonths = useMemo(() => {
    const months = resolvedReport?.data?.campaign_performance_totals?.range_helper?.available_months;
    return Array.isArray(months) ? [...months].sort() : [];
  }, [resolvedReport?.data?.campaign_performance_totals?.range_helper?.available_months]);

  // Lazy-load raw campaign data when the Meta report is first opened.
  // Phase 1: fetch the most recent month immediately so the UI shows data fast.
  // Phase 2: fetch the remaining months one at a time (newest first) and merge
  //          each result into state as it arrives, rather than waiting for one
  //          giant all-months payload (~9 MB).
  useEffect(() => {
    if (resolvedReport?.data?.collection_name !== "shop_meta") return undefined;
    if (hasFullMetaPayload) return undefined;
    if (rawMetaLoadedRef.current) return undefined;
    if (!userId || !authToken) return undefined;

    const resolvedShopId = shopId || report?.shop_id || null;
    if (!resolvedShopId) return undefined;
    if (!metaAvailableMonths.length) return undefined;

    let active = true;
    rawMetaLoadedRef.current = true;
    setRawMetaLoading(true);

    // Most recent month is the last entry in the ascending-sorted array
    const initialFromMonth = metaAvailableMonths[metaAvailableMonths.length - 1] || null;
    const initialToMonth = initialFromMonth;

    const run = async () => {
      // Phase 1: load the most recent month for immediate display
      try {
        const response = await fetchMetaRawDataReport({
          userId,
          shopId: resolvedShopId,
          authToken,
          query: initialFromMonth ? { fromMonth: initialFromMonth, toMonth: initialToMonth } : null,
        });
        if (!active) return;
        const rawData = response?.report?.data?.campaign_performance_raw;
        if (rawData) {
          setReport((prev) => prev
            ? { ...prev, data: { ...(prev.data || {}), campaign_performance_raw: rawData } }
            : prev
          );
        }
      } catch {
        if (active) rawMetaLoadedRef.current = false;
        return;
      } finally {
        if (active) setRawMetaLoading(false);
      }

      // Phase 2: load each remaining month one at a time, newest → oldest
      const loadedKeys = new Set([initialFromMonth].filter(Boolean));
      const remaining = [...metaAvailableMonths]
        .filter((m) => !loadedKeys.has(m))
        .sort((a, b) => b.localeCompare(a));

      for (const month of remaining) {
        if (!active) break;
        try {
          const resp = await fetchMetaRawDataReport({
            userId,
            shopId: resolvedShopId,
            authToken,
            query: { fromMonth: month, toMonth: month },
          });
          if (!active) break;
          const newMonths = resp?.report?.data?.campaign_performance_raw?.months ?? [];
          if (!newMonths.length) continue;
          setReport((prev) => {
            if (!prev) return prev;
            const existing = Array.isArray(prev.data?.campaign_performance_raw?.months)
              ? prev.data.campaign_performance_raw.months : [];
            const existingKeys = new Set(existing.map((m) => m.month));
            const additions = newMonths.filter((m) => !existingKeys.has(m.month));
            if (!additions.length) return prev;
            return {
              ...prev,
              data: {
                ...prev.data,
                campaign_performance_raw: {
                  ...(prev.data.campaign_performance_raw || {}),
                  months: [...existing, ...additions],
                },
              },
            };
          });
        } catch {
          // silent — initial month data is already showing
        }
      }
    };

    run();

    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authToken, shopId, report?.shop_id, resolvedReport?.data?.collection_name, metaAvailableMonths]);

  // Homepage assessment follows the same progressive pattern as Meta:
  // show the last 7 days first, then hydrate the remaining windows one at a time.
  useEffect(() => {
    if (resolvedReport?.data?.collection_name !== "homepage_assessment") return undefined;
    if (!userId || !authToken) return undefined;

    const resolvedShopId = shopId || report?.shop_id || null;
    if (!resolvedShopId) return undefined;

    const availableWindows = Array.isArray(resolvedReport?.data?.available_windows)
      ? resolvedReport.data.available_windows
      : [];
    if (!availableWindows.length) return undefined;

    const loadedWindows = Array.isArray(resolvedReport?.data?.windows)
      ? resolvedReport.data.windows
      : [];
    const loadedKeys = new Set(loadedWindows.map((windowEntry) => windowEntry?.key).filter(Boolean));
    const reportGenerationKey = resolvedReport?.data?.generation_key || resolvedReport?._id || resolvedReport?.id || "";
    const loadKey = `${userId}:${resolvedShopId}:${reportGenerationKey}:${availableWindows.map((w) => w?.key).join(",")}`;

    if (homepageWindowsLoadKeyRef.current === loadKey) return undefined;
    const remainingWindows = availableWindows
      .filter((windowEntry) => windowEntry?.key && !loadedKeys.has(windowEntry.key))
      .sort((left, right) => {
        const leftDays = Number(left?.days);
        const rightDays = Number(right?.days);
        if (Number.isFinite(leftDays) && Number.isFinite(rightDays)) {
          return leftDays - rightDays;
        }
        return String(left?.key || "").localeCompare(String(right?.key || ""));
      });

    if (!remainingWindows.length) {
      homepageWindowsLoadKeyRef.current = loadKey;
      return undefined;
    }

    let active = true;
    homepageWindowsLoadKeyRef.current = loadKey;

    const run = async () => {
      for (const windowEntry of remainingWindows) {
        if (!active) break;
        try {
          const response = await fetchHomepageAssessmentReport({
            userId,
            shopId: resolvedShopId,
            authToken,
            traceId: reportOpenTraceId,
            query: {
              section: "assessment",
              windowKey: windowEntry.key,
              startDate: windowEntry.startDate || null,
              endDate: windowEntry.endDate || null,
            },
          });
          if (!active) break;

          const newWindows = Array.isArray(response?.report?.data?.windows)
            ? response.report.data.windows
            : [];
          if (!newWindows.length) continue;

          setReport((prev) => {
            if (!prev?.data) return prev;
            const existing = Array.isArray(prev.data.windows) ? prev.data.windows : [];
            const existingKeys = new Set(existing.map((entry) => entry?.key).filter(Boolean));
            const additions = newWindows.filter((entry) => entry?.key && !existingKeys.has(entry.key));
            if (!additions.length) return prev;
            return {
              ...prev,
              data: {
                ...prev.data,
                available_windows: prev.data.available_windows?.length
                  ? prev.data.available_windows
                  : response.report.data.available_windows || [],
                windows: [...existing, ...additions],
              },
            };
          });
        } catch (error) {
          console.warn("[homepage-assessment.progressive-window.error]", {
            windowKey: windowEntry?.key || null,
            error: error?.message || String(error),
          });
        }
      }
    };

    const hydrateTimer = window.setTimeout(run, 500);

    return () => {
      active = false;
      window.clearTimeout(hydrateTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    authToken,
    shopId,
    report?.shop_id,
    resolvedReport?.data?.collection_name,
    resolvedReport?.data?.generation_key,
    reportOpenTraceId,
  ]);


  const focusedViewRaw = String(searchParams.get("view") || "").trim();
  const focusedBenchmarkView =
    resolvedReport?.data?.collection_name === "shop_benchmark_report" &&
    Object.prototype.hasOwnProperty.call(BENCHMARK_VIEW_CONFIG, focusedViewRaw)
      ? focusedViewRaw
      : null;

  const reportTitle = getReportTitle(resolvedReport, t);
  const reportCollection = resolvedReport?.data?.collection_name || "";

  const handleReportWindowChange = useCallback(async (windowInfo = {}) => {
    setReportViewerWindow(windowInfo || null);
    const reportKey = windowInfo?.reportKey || null;
    const section = windowInfo?.section || null;
    const startDate = windowInfo?.startDate || null;
    const endDate = windowInfo?.endDate || null;
    const requestKey = [
      reportKey || "",
      section || "",
      startDate || "",
      endDate || "",
      windowInfo?.reportId || "",
      windowInfo?.generationKey || "",
      windowInfo?.windowKey || windowInfo?.key || "",
    ].join(":");

    if (!reportKey || !startDate || !endDate || !userId || !authToken) return;
    if (lastWindowFetchKeyRef.current === requestKey) return;

    let loader = null;
    if (reportKey === "conversion") {
      loader = () => fetchConversionAndPdpAssessmentReport({
        userId,
        shopId,
        authToken,
        startDate,
        endDate,
        windowKey: windowInfo?.windowKey || windowInfo?.key || null,
        reportId: windowInfo?.reportId || null,
        generationKey: windowInfo?.generationKey || null,
      });
    } else if (reportKey === "homepageAssessment") {
      loader = () => fetchHomepageAssessmentReport({
        userId,
        shopId,
        authToken,
        query: { startDate, endDate, section: "assessment" },
      });
    } else if (reportKey === "socialMedia" && section === "assessment") {
      loader = () => fetchSocialMediaAssessmentReport({
        userId,
        shopId,
        authToken,
        query: { startDate, endDate, section: "assessment" },
      });
    } else if (reportKey === "socialMedia" && section === "report") {
      loader = () => fetchSocialMediaRawReport({
        userId,
        shopId,
        authToken,
        query: { startDate, endDate, section: "report" },
      });
    }

    if (!loader) return;
    lastWindowFetchKeyRef.current = requestKey;
    const seq = ++windowFetchSeqRef.current;
    setWindowLoadingKey(requestKey);
    console.log("[window-change.fetch]", {
      reportKey,
      section,
      startDate,
      endDate,
      reportId: windowInfo?.reportId || null,
      generationKey: windowInfo?.generationKey || null,
    });
    try {
      const result = await loader();
      if (seq !== windowFetchSeqRef.current) return;
      const nextReport = result?.report || result || null;
      console.log("[window-change.result]", {
        requestedStartDate: startDate,
        requestedEndDate: endDate,
        resultReportId: nextReport?._id || nextReport?.id || null,
        resultPeriod: nextReport?.data?.reporting_period || null,
        resultGenerationKey: nextReport?.data?.generation_key || null,
      });
      if (nextReport?.data) {
        if (reportKey === "conversion") {
          // Merge the new window into the existing windows array so previously loaded
          // windows are not lost when the user switches between windows.
          const incomingWindows = Array.isArray(nextReport.data.windows)
            ? nextReport.data.windows
            : [];
          setReport((prev) => {
            if (!prev?.data) return nextReport;
            const existing = Array.isArray(prev.data.windows) ? prev.data.windows : [];
            const existingKeys = new Set(existing.map((w) => w?.key).filter(Boolean));
            const additions = incomingWindows.filter((w) => w?.key && !existingKeys.has(w.key));
            const updatedWindows = additions.length
              ? [...existing, ...additions]
              : existing;
            return {
              ...prev,
              data: {
                ...prev.data,
                default_window_key: nextReport.data.default_window_key || prev.data.default_window_key,
                windows: updatedWindows,
              },
            };
          });
        } else {
          setReport(nextReport);
        }
      }
    } catch (error) {
      if (seq !== windowFetchSeqRef.current) return;
      console.warn("[window-change.error]", {
        reportKey,
        section,
        startDate,
        endDate,
        error: error?.message || String(error),
      });
    } finally {
      if (seq === windowFetchSeqRef.current) {
        setWindowLoadingKey(null);
      }
    }
  }, [authToken, shopId, userId]);

  useEffect(() => {
    if (!resolvedReport) return;
    const collection = resolvedReport?.data?.collection_name || "";
    const refreshKey = REFRESH_KEY_BY_COLLECTION[collection] || null;
    setPageHeader({
      title: reportTitle,
      updatedAt: resolvedReport.updatedAt || resolvedReport.createdAt || resolvedReport.createdDate || null,
      onRefresh: refreshKey && userId && shopId
        ? async () => {
            if (resolvedReport?.data?.collection_name === "google_search_console") {
              console.log("[GSC_REFRESH][frontend] clicked", {
                userId,
                shopId,
                strategyId: resolvedReport?.data?.strategy_id || null,
                forceRefresh: true,
              });
              const refreshed = await refreshGscReport({
                userId,
                shopId,
                strategyId: resolvedReport?.data?.strategy_id || null,
                authToken,
                forceRefresh: true,
              });
              if (refreshed?.report) {
                setReport(refreshed.report);
              }
              setReportVersion((v) => v + 1);
              return;
            }
            await forceRefreshReport({ reportKey: refreshKey, userId, shopId, authToken });
            setReportVersion((v) => v + 1);
          }
        : null,
    });
  }, [resolvedReport, reportTitle, setPageHeader, userId, shopId, authToken]);
  const reportChatScope = REPORT_CHAT_CONTEXT_MAP[reportCollection] || REPORT_CHAT_CONTEXT_MAP.benchmark;
  const reportContentContext = useMemo(() => {
    const factory = REPORT_CONTENT_CONTEXT_MAP[reportCollection];
    if (!factory) return null;
    return (
      factory({
        summaryId: searchParams.get("summary"),
        activeGscTab,
        activeRevenueTab,
      }) || null
    );
  }, [reportCollection, searchParams, activeGscTab, activeRevenueTab]);
  const baseChatContextTrail = useMemo(() => {
    const baseTrail = [...(reportChatScope?.trail || REPORT_CHAT_CONTEXT_MAP.benchmark.trail)];
    if (resolvedReport) {
      baseTrail.push({
        key: `report:${reportCollection || reportId}`,
        label: reportTitle,
        type: "report",
        collectionName: reportCollection || null,
        reportId: String(resolvedReport?._id || resolvedReport?.id || reportId || ""),
      });
    }
    if (reportContentContext) {
      baseTrail.push({
        ...reportContentContext,
        collectionName: reportCollection || null,
        reportId: String(resolvedReport?._id || resolvedReport?.id || reportId || ""),
      });
    }
    if (focusedBenchmarkView) {
      const focusedMeta = BENCHMARK_VIEW_CONFIG[focusedBenchmarkView];
      baseTrail.push({
        key: `focus:${focusedBenchmarkView}`,
        label: focusedMeta.label,
        type: focusedMeta.type,
        collectionName: reportCollection || null,
        reportId: String(resolvedReport?._id || resolvedReport?.id || reportId || ""),
      });
    }
    return baseTrail;
  }, [
    resolvedReport,
    reportChatScope,
    reportTitle,
    reportCollection,
    reportId,
    focusedBenchmarkView,
    reportContentContext,
  ]);
  const combinedChatContextTrail = useMemo(() => {
    const next = [...baseChatContextTrail];
    const autoWindowContext = buildReportWindowChatContext({
      reportId: String(resolvedReport?._id || resolvedReport?.id || reportId || ""),
      collectionName: reportCollection || null,
      reportTitle,
      windowInfo: reportViewerWindow,
    });
    if (autoWindowContext && !next.some((existing) => existing.key === autoWindowContext.key)) {
      next.push(autoWindowContext);
    }
    extraChatContexts.forEach((item) => {
      if (!item?.key) return;
      if (!next.some((existing) => existing.key === item.key)) {
        next.push(item);
      }
    });
    return next;
  }, [
    baseChatContextTrail,
    extraChatContexts,
    reportCollection,
    reportId,
    reportTitle,
    reportViewerWindow,
    resolvedReport,
  ]);
  const visibleChatContextTrail = useMemo(() => {
    const next = combinedChatContextTrail.filter(
      (item) => !dismissedContextKeys.includes(item.key),
    );
    if (next.length) return next;
    return [{ key: "main", label: "Main", type: "main" }];
  }, [combinedChatContextTrail, dismissedContextKeys]);
  const activeChatContext = useMemo(
    () =>
      sanitizeChatContext(visibleChatContextTrail) || {
        key: "main",
        label: "Main",
        type: "main",
        activeKey: "main",
        items: [{ key: "main", label: "Main", type: "main" }],
        reportIds: [],
        reportId: null,
        mode: "single",
      },
    [visibleChatContextTrail],
  );
  const activeContextKeys = useMemo(
    () =>
      new Set(
        (activeChatContext?.items || [])
          .map((item) => item?.key)
          .filter(Boolean),
      ),
    [activeChatContext],
  );
  const availableContextOptions = useMemo(
    () =>
      contextOptions.filter(
        (entry) =>
          entry?.key &&
          !activeContextKeys.has(entry.key) &&
          !visibleChatContextTrail.some((context) => context.key === entry.key)
      ),
    [activeContextKeys, contextOptions, visibleChatContextTrail]
  );

  useEffect(() => {
    if (readyLogRef.current) return;
    if (reportLoading || reportError || !resolvedReport?.data) return;
    // For Meta reports with split fetch, raw data is the primary view
    const isMeta = resolvedReport?.data?.collection_name === "shop_meta";
    const isMetaSplitFetch = isMeta && (prefetchedReport?.data?.collection_name === "shop_meta" && isPrefetchedMatch);
    if (isMeta && !isMetaSplitFetch && !hasFullMetaPayload) return;
    readyLogRef.current = true;
    console.log("[report-open][frontend] detail.ready", {
      traceId: reportOpenTraceId,
      reportId: resolvedReport?._id || resolvedReport?.id || reportId || null,
      collectionName: resolvedReport?.data?.collection_name || null,
      clickToReadyMs: reportOpenTrace?.clickStartedAtMs
        ? Date.now() - Number(reportOpenTrace.clickStartedAtMs)
        : null,
    });
  }, [
    reportLoading,
    reportError,
    resolvedReport,
    hasFullMetaPayload,
    reportOpenTraceId,
    reportOpenTrace?.clickStartedAtMs,
    reportId,
    prefetchedReport,
    isPrefetchedMatch,
  ]);

  const brackets = report?.data?.date_brackets;

  const resetSpecificContext = useCallback((contextKey, contextLabel) => {
    setDismissedContextKeys((current) =>
      current.includes(contextKey) ? current : [...current, contextKey],
    );
    setExtraChatContexts((current) => {
      const label = contextLabel || current.find((c) => c?.key === contextKey)?.label;
      if (label) {
        window.dispatchEvent(
          new CustomEvent("zeqi:chat-context-notification", {
            detail: { type: "removed", label, key: contextKey },
          }),
        );
      }
      return current.filter((entry) => entry?.key !== contextKey);
    });
  }, []);

  const addChatContext = useCallback((nextContext, { silent = false } = {}) => {
    if (!nextContext?.key) return;
    console.log("[ReportDetailPage:addChatContext] called", { key: nextContext.key, type: nextContext.type, label: nextContext.label });
    setDismissedContextKeys((current) =>
      current.filter((entry) => entry !== nextContext.key)
    );
    setExtraChatContexts((current) => {
      const replaceSingletonType =
        nextContext.type === "pdp-product" || nextContext.type === "report-window";
      const remaining = current.filter((entry) => {
        if (entry?.key === nextContext.key) return false;
        if (replaceSingletonType && entry?.type === nextContext.type) return false;
        return true;
      });
      return [...remaining, nextContext];
    });
    if (!silent && nextContext?.label) {
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-context-notification", {
          detail: { type: "added", label: nextContext.label, key: nextContext.key },
        }),
      );
    }
    setContextMenuOpen(false);
  }, []);

  const handleOpenViewerContext = useCallback((channelId, nextContext = null) => {
    if (!nextContext?.key) return;
    addChatContext(nextContext);
  }, [addChatContext]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleAutoChatContext = (event) => {
      const { context } = event?.detail || {};
      if (!context?.key) return;
      const currentReportId = String(resolvedReport?._id || resolvedReport?.id || reportId || "");
      const reportIdMismatch = context.reportId && String(context.reportId) !== currentReportId;
      console.log("[ReportDetailPage:autoContext] received zeqi:add-chat-context", { contextKey: context.key, contextType: context.type, label: context.label, currentReportId, contextReportId: context.reportId || null, reportIdMismatch });
      if (reportIdMismatch) return;
      addChatContext(context, { silent: true });
    };
    window.addEventListener("zeqi:add-chat-context", handleAutoChatContext);
    return () => {
      window.removeEventListener("zeqi:add-chat-context", handleAutoChatContext);
    };
  }, [addChatContext, reportId, resolvedReport?._id, resolvedReport?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleRemoveChatContext = (event) => {
      const { key, type } = event?.detail || {};
      console.log("[ReportDetailPage:removeContext] received zeqi:remove-chat-context", { key, type });
      setExtraChatContexts((current) => {
        if (key) return current.filter((c) => c?.key !== key);
        if (type) return current.filter((c) => c?.type !== type);
        return current;
      });
    };
    window.addEventListener("zeqi:remove-chat-context", handleRemoveChatContext);
    return () => {
      window.removeEventListener("zeqi:remove-chat-context", handleRemoveChatContext);
    };
  }, []);

  useEffect(() => {
    extraContextsHydratedRef.current = false;
    setDismissedContextKeys([]);
    setExtraChatContexts(readStoredReportChatContexts(userId, reportId));
    setContextMenuOpen(false);
    extraContextsHydratedRef.current = true;
  }, [reportId, userId]);

  useEffect(() => {
    if (!extraContextsHydratedRef.current) return;
    writeStoredReportChatContexts(userId, reportId, extraChatContexts);
  }, [extraChatContexts, reportId, userId]);

  const handleContextDrop = useCallback(
    (event) => {
      event.preventDefault();
      const droppedContext = readDraggedChatContext(event);
      setIsContextDropActive(false);
      if (!droppedContext?.key) return;
      addChatContext(droppedContext);
    },
    [addChatContext]
  );

  useEffect(() => {
    setChatContextPickMode(contextMenuOpen);
    return () => setChatContextPickMode(false);
  }, [contextMenuOpen]);

  useEffect(() => {
    let active = true;
    if (!userId || !authToken || !shopId) return undefined;

    const resolvedReportId = String(
      resolvedReport?._id || resolvedReport?.id || reportId || ""
    ).trim();

    getChannelChatContextOptions({
      userId,
      shopId,
      scope: "report",
      reportId: resolvedReportId || null,
      authToken,
    })
      .then((response) => {
        if (!active) return;
        setContextOptions(Array.isArray(response?.options) ? response.options : []);
      })
      .catch(() => {
        if (!active) return;
        setContextOptions([]);
      });

    return () => {
      active = false;
    };
  }, [authToken, reportId, resolvedReport, shopId, userId]);

  useEffect(() => {
    if (!contextMenuOpen) return undefined;

    // Close on click/touchend so beginning a drag on a right-rail item
    // does not immediately exit pick mode before the drag can start.
    const handleClickOutside = (event) => {
      if (contextMenuRef.current?.contains(event.target)) return;
      setContextMenuOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchend", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchend", handleClickOutside);
    };
  }, [contextMenuOpen]);

  const startResize = useCallback((startY) => {
    const shell = splitRef.current;
    if (!shell) return;

    const shellRect = shell.getBoundingClientRect();
    const shellHeight = shellRect.height || 1;
    const initialTopHeight = shellHeight * topFlex;

    const updateFromPointer = (clientY) => {
      const delta = clientY - startY;
      const nextTopHeight = Math.min(
        Math.max(initialTopHeight + delta, 140),
        shellHeight - 120,
      );
      setTopFlex(nextTopHeight / shellHeight);
      setChatPaneState("default");
    };

    const stopMouseDrag = () => {
      window.removeEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
      window.removeEventListener("mouseup", stopMouseDrag);
      delete window[DRAG_START_MOUSE_KEY];
    };

    const stopTouchDrag = () => {
      window.removeEventListener("touchmove", window[DRAG_START_TOUCH_KEY]);
      window.removeEventListener("touchend", stopTouchDrag);
      delete window[DRAG_START_TOUCH_KEY];
    };

    window[DRAG_START_MOUSE_KEY] = (event) => updateFromPointer(event.clientY);
    window[DRAG_START_TOUCH_KEY] = (event) => {
      const touch = event.touches[0];
      if (touch) updateFromPointer(touch.clientY);
    };

    window.addEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
    window.addEventListener("mouseup", stopMouseDrag);
    window.addEventListener("touchmove", window[DRAG_START_TOUCH_KEY], { passive: true });
    window.addEventListener("touchend", stopTouchDrag, { passive: true });
  }, [topFlex]);

  const topPaneStyle =
    chatPaneState === "maximized"
      ? { flex: "0 0 0%", minHeight: 0 }
      : isChatClosed
        ? { flex: "1 1 0%", minHeight: 0 }
        : { flex: `${topFlex} 1 0%` };
  const chatPaneStyle =
    chatPaneState === "minimized"
      ? { flex: "0 0 54px" }
      : chatPaneState === "maximized"
        ? { flex: "1 1 0%" }
        : { flex: `${1 - topFlex} 1 0%` };

  return (
    <section className={`${pageStyles.page} ${styles.wrapper}`} data-page="report-detail">
      {reportLoading && !report ? <p className={styles.stateText}>{t("ReportsPage.loading") || "Loading reports..."}</p> : null}
      {reportError ? (
        <p className={styles.stateText}>
          {t("ReportsPage.loadErrorPrefix") || "Failed to load reports:"} {reportError.message}
        </p>
      ) : null}
      {!reportLoading && !reportError && !report ? (
        <p className={styles.stateText}>{t("ReportsPage.reportDetails") || "Report not found."}</p>
      ) : null}

      {report ? (
        <div ref={splitRef} className={styles.splitShell}>
          <div
            className={`${styles.viewerPane} ${
              chatPaneState === "maximized" ? styles.viewerPaneHidden : ""
            }`}
            style={topPaneStyle}
          >
            <div className={styles.viewerCard}>
              <div className={styles.viewerHeader}>
                {fromDailyOverview ? (
                  <div className={styles.viewerBackRow}>
                    <button
                      type="button"
                      className={styles.backButton}
                      onClick={() => navigate(dailyOverviewReturnTo)}
                    >
                      <span className={styles.backButtonArrow}>←</span>
                      <span className={styles.backButtonText}>Back to {dailyOverviewLabel}</span>
                    </button>
                  </div>
                ) : null}
                {report?.data?.collection_name === "google_search_console" && (
                  <div className={styles.tabRow}>
                    {GSC_TAB_CONFIG.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`${styles.tabButton} ${
                          activeGscTab === tab.key ? styles.tabButtonActive : ""
                        }`}
                        onClick={() => setActiveGscTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {report?.data?.collection_name === "revenue_reality_full_report" && (
                  <div className={`${styles.tabRow} ${styles.tabRowScrollable}`}>
                    {REVENUE_REALITY_TAB_CONFIG.map((tab) => {
                      const isLockedForFree =
                        !subSummary.isMonthlySubscription &&
                        FREE_LOCKED_REVENUE_TABS.has(tab.key);
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          className={`${styles.tabButton} ${
                            activeRevenueTab === tab.key ? styles.tabButtonActive : ""
                          }`}
                          onClick={() => setActiveRevenueTab(tab.key)}
                        >
                          {tab.label}
                          {isLockedForFree ? " Locked" : ""}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div
                className={`${styles.viewerScroll} ${
                  isChatClosed ? styles.viewerScrollChatClosed : ""
                }`}
              >
                <ReportViewerContent
                  report={resolvedReport}
                  focusedView={focusedBenchmarkView}
                  activeTab={activeGscTab}
                  activeRevenueTab={activeRevenueTab}
                  isMonthlySubscription={subSummary.isMonthlySubscription}
                  reportLoading={reportLoading || Boolean(windowLoadingKey)}
                  hasFullMetaPayload={hasFullMetaPayload}
                  onOpenChannel={handleOpenViewerContext}
                  onWindowChange={handleReportWindowChange}
                  authToken={authToken}
                />
              </div>
            </div>
          </div>

          {!isChatClosed ? <div className={styles.paneDivider} aria-hidden="true">
            <button
              type="button"
              className={styles.dragHandle}
              onMouseDown={(event) => startResize(event.clientY)}
              onTouchStart={(event) => {
                const touch = event.touches[0];
                if (touch) startResize(touch.clientY);
              }}
              aria-label="Resize report chat"
            />
            <div className={styles.dividerControls}>
              <div className={styles.chatTitleWrap}>
                <span className={styles.chatTitle}>Zeqi chat</span>
                <div
                  className={`${styles.chatContextTrail} ${
                    isContextDropActive ? styles.chatContextTrailDropActive : ""
                  }`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsContextDropActive(true);
                  }}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setIsContextDropActive(true);
                  }}
                  onDragLeave={(event) => {
                    if (event.currentTarget.contains(event.relatedTarget)) return;
                    setIsContextDropActive(false);
                  }}
                  onDrop={handleContextDrop}
                >
                  {visibleChatContextTrail.map((context) => {
                    const isDismissible = context.key !== "main";
                    return (
                      <span key={context.key} className={styles.chatContextPill}>
                        <span className={styles.chatContextPillText}>{context.label}</span>
                        {isDismissible ? (
                          <button
                            type="button"
                            className={styles.chatContextPillReset}
                            onClick={() => resetSpecificContext(context.key, context.label)}
                            aria-label={`Clear ${context.label} context`}
                          >
                            <X size={12} strokeWidth={2} />
                          </button>
                        ) : null}
                      </span>
                    );
                  })}
                  <div
                    ref={contextMenuRef}
                    className={styles.contextMenuWrap}
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`${styles.chatContextAddButton} ${
                        contextMenuOpen ? styles.chatContextAddButtonActive : ""
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setContextMenuOpen((current) => !current);
                      }}
                      aria-label="Toggle chat context pick mode"
                      title={
                        contextMenuOpen
                          ? "Exit chat context pick mode"
                          : "Enter chat context pick mode"
                      }
                    >
                      <Plus size={12} strokeWidth={2.2} />
                    </button>
                  </div>
                </div>
              </div>
              <div className={styles.controlButtons}>
                <button
                  type="button"
                  className={styles.controlButton}
                  onClick={handleExpandChat}
                  aria-label="Expand report chat"
                >
                  <ArrowUp size={16} strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  className={styles.controlButton}
                  onClick={handleExpandReports}
                  aria-label="Restore report chat"
                >
                  <ArrowDown size={16} strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  className={styles.controlButton}
                  onClick={() => setChatPaneState("minimized")}
                  aria-label="Close report chat"
                >
                  <X size={16} strokeWidth={1.75} />
                </button>
              </div>
            </div>
          </div> : null}

          {!isChatClosed ? <div
            className={`${styles.chatPane} ${
              chatPaneState === "minimized"
                ? styles.chatPaneMinimized
                : chatPaneState === "maximized"
                  ? styles.chatPaneMaximized
                  : ""
            }`}
            style={chatPaneStyle}
            >
              <div className={styles.chatBody}>
              <ChatPage
                inline
                variant="inline"
                channelId={reportChatScope.channelId || "main"}
                chatContext={activeChatContext}
                inputPlaceholder={`Ask about ${activeChatContext.label}...`}
                viewerTimeWindow={reportViewerWindow}
              />
              </div>
          </div> : null}
        </div>
      ) : null}
      {report && isChatClosed ? (
        <div className={styles.chatReopenDock}>
          <button
            type="button"
            className={styles.chatReopenField}
            onClick={() => setChatPaneState("default")}
            aria-label="Reopen Zeqi chat"
            title="Reopen Zeqi chat"
          >
            <span className={styles.chatReopenFieldIcon}>
              <Plus size={18} strokeWidth={1.9} />
            </span>
            <span className={styles.chatReopenFieldText}>
              Ask about {activeChatContext.label}...
            </span>
            <span className={styles.chatReopenFieldSend}>
              <Send size={16} strokeWidth={1.9} />
            </span>
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default ReportDetailPage;
